import { createRoute, Navigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { authClient, useSession } from "../auth-client";
import { Route as rootRoute } from "./__root";
import { Button } from "@/frontend/components/ui/button";
import { OnboardingLayout, LoadingScreen } from "@/frontend/components/onboarding/Onboarding";
import { useQuery } from "@tanstack/react-query";
import { 
  Loader2, 
  ArrowRight, 
  Shield, 
  Lock, 
  Fingerprint,
} from "lucide-react";
import { orpc } from "@/frontend/orpc-client";

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/patient",
  component: PatientPage,
});

function PatientPage() {
  const { data: session, isPending } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [idmeLoading, setIdmeLoading] = useState(false);
  const actorContextQuery = useQuery({
    ...orpc.org.getActorContext.queryOptions({ input: {} }),
    enabled: !!session,
  });

  const activeMemberQuery = useQuery({
    ...orpc.org.getActiveMember.queryOptions({ input: {} }),
    enabled: !!session && session.user.role !== "admin",
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlError = params.get("error");
    const errorDescription = params.get("error_description");
    
    if (urlError) {
      setError(errorDescription || urlError);
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  if (isPending) {
    return <LoadingScreen />;
  }

  if (session?.user?.role === "admin") {
    return <Navigate to="/dashboard/patients" search={{ patientMembershipId: undefined }} />;
  }

  if (actorContextQuery.isLoading) {
    return <LoadingScreen />;
  }

  if (actorContextQuery.data?.actorType === "orgMember") {
    return <Navigate to="/dashboard/patients" search={{ patientMembershipId: undefined }} />;
  }

  if (actorContextQuery.data?.actorType === "patient") {
    return <Navigate to="/sign" search={{ continue: false }} />;
  }

  const memberRole = activeMemberQuery.data?.role ?? "";
  const memberRoles = memberRole.split(",").map((r) => r.trim()).filter(Boolean);
  if (session && (memberRoles.includes("admin") || memberRoles.includes("owner"))) {
    return <Navigate to="/dashboard/patients" search={{ patientMembershipId: undefined }} />;
  }

  if (session) {
    return <Navigate to="/sign" search={{ continue: false }} />;
  }

  const handleIdmeLogin = async () => {
    setIdmeLoading(true);
    try {
      await authClient.signIn.oauth2({
        providerId: "id.me",
        callbackURL: `${window.location.origin}/patient`
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setIdmeLoading(false);
    }
  };

  return (
    <OnboardingLayout>
      <div className="flex flex-col px-6 py-6">
        <div className="size-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
          <Fingerprint className="size-7 text-primary" />
        </div>
        
        <h1 className="text-2xl font-bold tracking-tight mb-2">
          Verify your identity
        </h1>
        
        <p className="text-muted-foreground mb-6">
          Identity verification is required to ensure accurate and secure transfer of your health records.
        </p>
        
        {error && (
          <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm mb-6">
            {error}
          </div>
        )}
        
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
            <Shield className="size-5 text-primary mt-0.5 shrink-0" />
            <p className="text-sm">ID.me is trusted by over 80 million people for secure identity verification.</p>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
            <Lock className="size-5 text-primary mt-0.5 shrink-0" />
            <p className="text-sm">ID.me will not receive a copy of your medical records.</p>
          </div>
        </div>
        
        <div className="mt-8">
          <Button 
            onClick={handleIdmeLogin}
            disabled={idmeLoading}
            className="w-full"
            size="lg"
          >
            {idmeLoading ? (
              <>
                <Loader2 className="animate-spin" data-icon="inline-start" />
                Connecting...
              </>
            ) : (
              <>
                Verify with ID.me
                <ArrowRight data-icon="inline-end" />
              </>
            )}
          </Button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
